<template>
    <div class="">
        <vue-simple-header>
            <span slot="headerLabel" v-if="post">
                Created in Group <strong><simple-link :target="post.group"></simple-link></strong>
                by <strong><simple-link :target="post.created_by"></simple-link></strong> on
                {{ post.date | gbdate }}
            </span>
            <span slot="panelRight">
                <vue-folder-button
                        v-if="visibleIcons.info"
                        icon-class="fa-info"
                        :target="{ ...$route, name:'GroupPostDetailPageInfo'}"
                        :active="$route.meta.name==='info'"
                        :is-router-link="true"
                ></vue-folder-button>
                <like-with-popover
                        v-if="post"
                        v-show="visibleIcons.like"
                        :initial-entry="post"
                        :likes-entry-point="likesEntryPoint"
                ></like-with-popover>
                <vue-folder-button
                        v-if="visibleIcons.comments"
                        icon-class="fa-comments-o"
                        :text="commentsButtonText"
                        :active="$route.meta.name==='comments'"
                        :target="{...$route, name:'GroupPostDetailPageComments'}"
                        :is-router-link="true"
                >
                    <span slot="text" v-html="commentsButtonText"></span>
                </vue-folder-button>
                <vue-folder-button
                        v-if="visibleIcons.locations"
                        icon-class="fa-map-marker"
                        :text="locationsButtonText"
                        tabindex="0"
                        @click.native="editLocations"
                >
                    <span slot="text" v-html="locationsButtonText"></span>
                </vue-folder-button>
                <vue-folder-button
                        v-if="visibleIcons.viewEditMode"
                        :icon-class="editMode?'fa-eye':'fa-pencil'"
                        aria-label="Edit Mode"
                        role="button"
                        tabindex="0"
                        @click.native="toggleEditMode"
                >
                    <span slot="text">&nbsp;</span>
                </vue-folder-button>
                <vue-folder-button
                        v-if="visibleIcons.remove"
                        icon-class="fa-trash-o"
                        color="red"
                        tabindex="0"
                        @click.native="removePost"
                ></vue-folder-button>
                <vue-folder-button
                        v-if="visibleIcons.showMore"
                        icon-class="fa-ellipsis-h"
                        tabindex="0"
                        @click.native="expandButtons"
                ></vue-folder-button>
                <vue-folder-button
                        v-if="visibleIcons.back"
                        icon-class="fa-reply"
                        color="blue"
                        tabindex="0"
                        :target="backUrl"
                ></vue-folder-button>
            </span>
        </vue-simple-header>
        <div class="" v-if="initialized&&!accessError">
            <router-view
                    :post="post"
                    :edit-mode="editMode"
                    :is-admin="accessLevel===ACCESS_LEVELS.EDIT"
                    @onPostUpdate="onPostUpdate"
                    @onCommentsCountChanged="onCommentsCountChanged"
            ></router-view>
        </div>
        <div class="alert alert-danger margin-15-top" v-if="initialized&&accessError">
            <strong>Error</strong> Post not found or you do not have access to it
        </div>
    </div>
</template>

<script>
    import BackButtonMixin from "../../../../vue/mixins/BackButtonMixin";
    import Vue from "vue";
    import GroupActivityPostResource from "../../groups/group_activity_resource";
    import activityPostResourceFactory from "shared/activity_post/activityPostResourceFactory";
    import LikeWithPopover from "../../../../vue/common_components/LikeWithPopover";
    import likesResourceFactory from "shared/common/likesResourceFactory";
    import {simplePopupFactory} from "shared/simple_popups/simplePopupFactory";

    const ACCESS_LEVELS = {
        VIEW: 'view',
        EDIT: 'edit',
    };

    export default {
        name: "GroupPostDetailPage",
        components: {LikeWithPopover},
        mixins: [BackButtonMixin],
        props: {
            postId: {
                type: [Number, String],
            },
            isAdmin: {
                type: Boolean,
                default: false,
            }
        },
        data() {
            return {
                initialized: false,
                accessError: false,

                buttonsExpanded: false,
                editMode: false,

                post: undefined,
                accessLevel: ACCESS_LEVELS.VIEW,

                likePopoverOpened: false,
                popoverReady: false,
                isTouchDevise: false,
            };
        },
        computed: {
            ACCESS_LEVELS() {
                return ACCESS_LEVELS;
            },
            likesEntryPoint() {
                const $http = Vue.getAngularModule('$http');
                return likesResourceFactory('group_activity_post_likes')($http);
            },
            expandRequired() {
                let hiddenButtonsCount = 0;
                if (this.allowRemove) {
                    hiddenButtonsCount += 2;
                }
                if (this.locationsButtonHidden) {
                    hiddenButtonsCount++;
                }
                return hiddenButtonsCount > 1;
            },
            allowRemove() {
                return this.accessLevel === ACCESS_LEVELS.EDIT;
            },
            locationsButtonHidden() {
                return !this.post?.location_points_count;
            },
            visibleIcons() {
                const res = {
                    info: false,
                    like: false,
                    comments: false,
                    locations: false,
                    showMore: false,
                    viewEditMode: false,
                    remove: false,
                    back: false,
                };
                if (this.accessError) {
                    // when access error occur only back button is visible.
                    res.back = true;
                    return res;
                }
                if (!this.post) {
                    // while not loaded only back visible
                    res.back = true;
                    return res;
                }
                if (this.editMode) {
                    // in edit mode only view mode button available
                    res.viewEditMode = true;
                    return res;
                }

                res.back = true;
                res.comments = true;

                if (this.$route.meta.name === 'comments') {
                    res.info = true;
                    return res;
                }
                res.like = true;
                if (this.expandRequired && !this.buttonsExpanded) {
                    res.showMore = true;
                }
                if (!this.locationsButtonHidden) {
                    res.locations = true;
                }
                if (!this.expandRequired || this.buttonsExpanded) {
                    res.locations = true;
                    res.goals = true;
                    if (this.allowRemove) {
                        res.viewEditMode = true;
                        res.remove = true;
                    }
                }
                return res;
            },
            commentsButtonText() {
                if (this.post?.comments_count) {
                    return this.post.comments_count + '';
                } else {
                    return '&nbsp;';
                }
            },
            locationsButtonText() {
                if (this.post?.location_points_count) {
                    return this.post.location_points_count + '';
                } else {
                    return '&nbsp;';
                }
            },
        },
        methods: {
            expandButtons() {
                this.buttonsExpanded = true;
            },
            getPost() {
                const $http = Vue.getAngularModule('$http');
                const $rootScope = Vue.getAngularModule('$rootScope');
                $rootScope.show_dimmer();
                return GroupActivityPostResource($http).get_post(this.postId)
                    .then(({data}) => {
                        this.post = data.post;
                        if (data.is_admin) {
                            this.accessLevel = ACCESS_LEVELS.EDIT;
                        }
                        this.initialized = true;

                    }, err => {
                        this.initialized = true;
                        this.accessError = true;
                    })
                    .finally($rootScope.hide_dimmer);
            },
            toggleEditMode() {
                this.editMode = !this.editMode;
            },
            removePost() {
                const $http = Vue.getAngularModule('$http');
                const $rootScope = Vue.getAngularModule('$rootScope');
                simplePopupFactory.show_popup('Remove Post', 'Are you sure you want to remove this post', 'Remove', 'Cancel')
                    .then(yes => {
                        $rootScope.show_dimmer();
                        return activityPostResourceFactory('group_activity_post_manage')($http).remove_post(this.postId);
                    }, () => new Promise(() => {
                    }))
                    .then(resp => {
                        Vue.notifications.success('Deleted');
                        this.goBack();
                    }, err => {
                        Vue.notifications.error(err || 'Error');
                        $rootScope.hide_dimmer();
                    });
            },
            onPostUpdate(postData) {
                const $http = Vue.getAngularModule('$http');
                activityPostResourceFactory('group_activity_post_manage')($http).update_post(this.postId, postData)
                    .then(({data}) => {
                        this.post = data;
                        Vue.notifications.success('Updated');
                    }, err => {
                        Vue.notifications.error(err || 'Error');
                    });
            },
            onCommentsCountChanged(count) {
                this.post.comments_count = count;
            },
            editLocations() {
                const $http = Vue.getAngularModule('$http');
                const activityPostLocationsFactory = Vue.getAngularModule('activityPostLocationsFactory');

                GroupActivityPostResource($http).get_post_locations(this.postId)
                    .then(resp => {
                        const wrappedPost = new activityPostLocationsFactory.UserActivityPostLocations(
                            this.postId,
                            resp.data,
                            this.accessLevel === ACCESS_LEVELS.EDIT,
                            activityPostResourceFactory('group_activity_post_manage')($http),
                            this.post
                        );
                        wrappedPost.show_list();
                    });
            },
        },
        mounted() {
            this.getPost();
        },
        metaInfo() {
            if (this.post?.group) {
                return {
                    title: `Group Post - ${this.post.group.name}`
                };
            }
            return {
                title: 'Group Post'
            };
        }

    };
</script>

<style scoped lang="scss">

</style>
